import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe, useMyPreferences } from '../hooks/useMe';
import { api, ApiError } from '../lib/api';
import { currentEndpoint, disablePush, enablePush, isIos, pushSupport, type PushConfig } from '../lib/push';

function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : 'Something went wrong.';
}

/** "Notifications on this device" (Web Push) + install hints + the user's LINE channel switch. */
export function PushPanel() {
  const qc = useQueryClient();
  const me = useMe();
  const prefs = useMyPreferences();
  const support = pushSupport();
  const cfg = useQuery({ queryKey: ['push-config'], queryFn: () => api.get<PushConfig>('/api/v1/push/config') });
  const [endpoint, setEndpoint] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (support !== 'supported') return;
    let alive = true;
    void currentEndpoint().then((e) => alive && setEndpoint(e));
    return () => {
      alive = false;
    };
  }, [support]);

  const refresh = async () => {
    setEndpoint(await currentEndpoint());
    await qc.invalidateQueries({ queryKey: ['push-config'] });
  };
  const enable = useMutation({
    mutationFn: async () => {
      if (!cfg.data?.publicKey) throw new Error('Web Push is not configured on the server.');
      await enablePush(cfg.data.publicKey);
    },
    onSettled: refresh,
  });
  const disable = useMutation({ mutationFn: disablePush, onSettled: refresh });
  const test = useMutation({ mutationFn: () => api.post<{ sent: number }>('/api/v1/push/test') });

  const onThisDevice = Boolean(endpoint && cfg.data?.endpoints.includes(endpoint));
  const user = me.data?.user;
  const busy = enable.isPending || disable.isPending;
  const error = enable.error ?? disable.error ?? test.error;

  return (
    <div className="stack" style={{ gap: 6 }}>
      {support === 'ios-needs-install' ? (
        <p className="small" role="note">
          <strong>iPhone/iPad:</strong> tap <strong>Share</strong> → <strong>Add to Home Screen</strong>, then open
          MagicTradie from the new icon to turn on notifications (iOS 16.4 or later).
        </p>
      ) : support === 'unsupported' ? (
        <p className="small muted">This browser does not support app notifications. Use LINE alerts instead.</p>
      ) : cfg.isError ? (
        <p className="small muted">App notification status unavailable.</p>
      ) : !cfg.data ? (
        <p className="small muted">Loading…</p>
      ) : !cfg.data.configured ? (
        <p className="small muted">App notifications are not configured on the server yet.</p>
      ) : (
        <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
          {onThisDevice ? (
            <>
              <span className="badge badge--live">
                <span aria-hidden="true">📱</span> App notifications on this device
              </span>
              <button type="button" className="btn" disabled={test.isPending} onClick={() => test.mutate()}>
                {test.isPending ? 'Sending…' : 'Send test'}
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => disable.mutate()}>
                Turn off on this device
              </button>
            </>
          ) : (
            <button type="button" className="btn btn--primary" disabled={busy} onClick={() => enable.mutate()}>
              <span aria-hidden="true">📱</span> {busy ? 'Turning on…' : 'Turn on app notifications on this device'}
            </button>
          )}
          <span className="small muted">
            {cfg.data.mySubscriptions} device{cfg.data.mySubscriptions === 1 ? '' : 's'} on your account
          </span>
        </div>
      )}
      {!isIos() && support === 'supported' ? (
        <p className="small muted" style={{ margin: 0 }}>
          Android: browser menu ⋮ → <strong>Install app</strong> / <strong>Add to Home screen</strong> for an app icon.
        </p>
      ) : null}
      {me.data?.authRequired && user ? (
        <label className="small row" style={{ gap: 4 }}>
          <input
            type="checkbox"
            checked={user.notifyLine ?? true}
            disabled={prefs.isPending}
            onChange={(e) => prefs.mutate({ notifyLine: e.target.checked })}
          />
          Also send to my LINE
        </label>
      ) : null}
      {test.isSuccess ? (
        <p className="small pos" role="status" style={{ margin: 0 }}>
          Test sent — check this device.
        </p>
      ) : null}
      {error ? (
        <p className="small neg" role="alert" style={{ margin: 0 }}>
          {errorText(error)}
        </p>
      ) : null}
    </div>
  );
}
